import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { PageHero } from "@/components/site/PageShell";
import { CTASection } from "@/components/site/CTASection";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { canonical } from "@/components/site/site-config";
import { CITIES } from "@/components/site/constants";
import { ResponsivePicture, TAN_STANDING_SEAM_PINE_RIDGE } from "@/components/site/images";

export const Route = createFileRoute("/service-areas")({
  head: () => ({
    meta: [
      { title: "Roofing Service Areas in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "See the Citrus County and surrounding Florida communities served by Destination Roofing for roof repair, replacement and exterior roof services." },
      { property: "og:title", content: "Roofing Service Areas in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "Communities Destination Roofing serves across Citrus County, FL." },
      { property: "og:url", content: canonical("/service-areas") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: TAN_STANDING_SEAM_PINE_RIDGE.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/service-areas") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(breadcrumbListJsonLd([{ label: "Service Areas", href: "/service-areas" }])),
    }],
  }),
  component: Page,
});

const SUMMARIES: Record<string, string> = {
  "Beverly Hills": "Roof replacement, roof repair, shingle and metal roofing, and free inspections for Beverly Hills homeowners.",
  "Inverness": "Roof repair, replacement, shingle, metal and flat-roof service for Inverness homeowners.",
  "Crystal River": "Roof repair, replacement, metal, shingle and flat-roofing services for Crystal River homeowners.",
  "Homosassa": "Roof replacement, repair, metal and shingle roofing, and storm-damage response in Homosassa.",
  "Lecanto": "Lecanto roofing company for full replacement, repair, metal and shingle roofing, gutters and soffit.",
  "Hernando": "Roof repair, replacement, metal and shingle roofing, inspections, gutters and soffit services in Hernando.",
  "Floral City": "Family-owned roofing company providing roof repair, replacement, metal and shingle roofing in Floral City.",
  "Citrus Springs": "Roof replacement, roof repair, inspections, and metal and shingle roofing for Citrus Springs homeowners.",
  "Dunnellon": "Roof replacement, roof repair, metal and shingle roofing, and free roof inspections in Dunnellon.",
  "Spring Hill": "Roof replacement, repair, metal and shingle roofing for Spring Hill homeowners in Hernando County just south of Citrus County.",
  "Citrus Hills": "Roofing for larger and more complex residential roofs — valleys, hips, chimneys, skylights and ventilation — plus repair, shingle and metal replacement options.",
  "Pine Ridge": "Standing-seam and exposed-fastener metal roofing, Atlas shingle replacement and roof repair, including a real standing-seam metal roof we installed here.",
};

const NEARBY = [
  "Black Diamond",
  "Sugarmill Woods",
  "Brooksville",
];

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Service Areas", href: "/service-areas" }]} />
      <PageHero
        eyebrow="Service Areas"
        title="Roofing Service Areas in Citrus County, FL"
        subtitle="Destination Roofing is a family-owned Citrus County roofer. Here are the primary communities we serve."
      />
      <section className="mx-auto max-w-7xl px-4 py-14 md:py-20">
        <div className="grid gap-8 lg:grid-cols-3 items-start">
          <div className="lg:col-span-2 grid gap-4 sm:grid-cols-2">
            {CITIES.map(c => (
              <Link key={c.slug} to={c.slug} className="group rounded-xl border border-border bg-white p-5 hover:border-brand transition-colors shadow-[var(--shadow-card)]">
                <h2 className="font-display font-bold text-lg group-hover:text-brand inline-flex items-center gap-2">
                  <MapPin className="size-4 text-cta" /> Roofing Company in {c.city}, {c.state}
                </h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {SUMMARIES[c.city] ?? `Local roofing services in ${c.city}, ${c.state}.`}
                </p>
              </Link>
            ))}
          </div>
          <aside className="rounded-xl overflow-hidden border border-border bg-white shadow-[var(--shadow-card)]">
            <ResponsivePicture
              image={TAN_STANDING_SEAM_PINE_RIDGE}
              alt="Tan standing-seam metal roof installed on a new-construction home in Pine Ridge, Florida"
              sizes="(min-width: 1024px) 33vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <div className="p-5">
              <h3 className="font-display font-bold">Also serving nearby communities</h3>
              <p className="text-sm text-muted-foreground mt-2">
                We also complete roofing projects in {NEARBY.join(", ")} and other nearby communities across Citrus County and the Nature Coast. Not sure whether you're in our area? <Link to="/contact" className="text-brand underline">Send us a message</Link> and we'll let you know.
              </p>
            </div>
          </aside>
        </div>
      </section>
      <CTASection />
    </>
  );
}
